"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Col, Row } from "reactstrap"
import { Line, LineChart, Bar, BarChart, Area, AreaChart, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts"
import type { DateRangeAnalytics, DateRange } from "./types"
import AnalyticsService from "../../../Service/Analytics-Service"
import { formatCurrency } from "./analyticsHelpers"
import "../../CommonElements/DashboardTheme/dashboard-theme.css"

interface AnalyticalDataProps {
  dateRange: DateRange
  onDataLoad?: (data: DateRangeAnalytics | null) => void
}

const AnalyticalData: React.FC<AnalyticalDataProps> = ({ dateRange, onDataLoad }) => {
  const [analyticsData, setAnalyticsData] = useState<DateRangeAnalytics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true)
        const data = await AnalyticsService.getDateRangeAnalytics(dateRange.startDate, dateRange.endDate)
        setAnalyticsData(data)
        if (onDataLoad) {
          onDataLoad(data)
        }
      } catch (error) {
        console.error("Error fetching analytics:", error)
        setAnalyticsData(null)
        if (onDataLoad) {
          onDataLoad(null)
        }
      } finally {
        setLoading(false)
      }
    }

    fetchAnalytics()
  }, [dateRange]) // Removed onDataLoad from dependencies to prevent infinite loop

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white px-3 py-2 border-0 rounded shadow-sm" style={{ fontSize: "0.82rem" }}>
          <p className="text-muted mb-1">{new Date(label).toLocaleDateString()}</p>
          <p className="mb-0 fw-bold" style={{ color: payload[0].color || "#4f46e5" }}>
            {payload[0].value}
          </p>
        </div>
      )
    }
    return null
  }

  // Show placeholder data if analyticsData is null or loading
  const displayStats = analyticsData || {
    totalEntries: 0,
    totalExits: 0,
    totalVehicleRevenue: 0,
    totalVerifications: 0,
    totalBadgePrints: 0,
    totalFreeOriginalBadges: 0,
    totalPaidCopyBadges: 0,
    totalPaidCopyBadgeRevenue: 0,
    totalTicketSalesCount: 0,
    totalTicketRevenue: 0,
    totalUsedTickets: 0,
    totalUnusedTickets: 0,
    totalPaidCheckouts: 0,
    totalWhitelistedCheckouts: 0,
    ticketTrends: [],
    vehicleTrends: [],
    badgeVerifications: [],
  }

  const totalTicketSales = displayStats.ticketTrends.reduce((sum, item) => sum + item.newBought, 0)
  const totalVehicleEntries = displayStats.vehicleTrends.reduce((sum, item) => sum + item.entered, 0)
  const totalVerifications = displayStats.badgeVerifications.reduce((sum, item) => sum + item.verifications, 0)

  const vehiclesStillInParking = displayStats.totalEntries - displayStats.totalExits

  return (
    <div className="analytics-data">
      {/* First Section: Overview banner for the selected date range */}
      <div className="db-section">
        <div className="db-live-banner db-bg-brand">
          <div className="db-live-banner-head">
            <div className="db-live-banner-title">
              <i className="fa fa-chart-line"></i>
              Analytics Overview
            </div>
            <div className="db-live-pill">
              <i className="fa fa-calendar me-1"></i>
              {dateRange.startDate} → {dateRange.endDate}
            </div>
          </div>

          {loading ? (
            <Row className="g-2">
              {Array.from({ length: 8 }).map((_, index) => (
                <Col lg="3" md="4" sm="6" xs="6" key={index} className="mb-2">
                  <div className="db-live-metric">
                    <div
                      className="db-skeleton mb-1"
                      style={{ height: "22px", width: "100%", background: "rgba(255,255,255,0.2)" }}
                    ></div>
                    <div
                      className="db-skeleton"
                      style={{ height: "10px", width: "70%", margin: "0 auto", background: "rgba(255,255,255,0.2)" }}
                    ></div>
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <Row className="g-3">
              {/* First Column: Vehicle Data */}
              <Col lg="6" md="6" sm="12">
                <div className="db-live-group">
                  <div className="db-live-group-title">
                    <i className="fa fa-car"></i>
                    Vehicle Activity
                  </div>
                  <Row className="g-2">
                    <Col lg="6" md="12" sm="6" xs="6">
                      <div className="db-live-metric">
                        <div className="db-live-value">{displayStats.totalEntries}</div>
                        <div className="db-live-label">
                          <i className="fa fa-sign-in"></i>
                          Check-ins
                        </div>
                      </div>
                    </Col>
                    <Col lg="6" md="12" sm="6" xs="6">
                      <div className="db-live-metric">
                        <div className="db-live-value">{displayStats.totalExits}</div>
                        <div className="db-live-label">
                          <i className="fa fa-sign-out"></i>
                          Checked Out
                        </div>
                      </div>
                    </Col>
                    <Col lg="6" md="12" sm="6" xs="6">
                      <div className="db-live-metric">
                        <div className="db-live-value">{vehiclesStillInParking}</div>
                        <div className="db-live-label">
                          <i className="fa fa-parking"></i>
                          Not Yet Checked Out
                        </div>
                      </div>
                    </Col>
                    <Col lg="6" md="12" sm="6" xs="6">
                      <div className="db-live-metric">
                        <div className="db-live-value">{formatCurrency(displayStats.totalVehicleRevenue)}</div>
                        <div className="db-live-label">
                          <i className="fa fa-money"></i>
                          Vehicle Revenue
                        </div>
                      </div>
                    </Col>
                    <Col lg="6" md="12" sm="6" xs="6">
                      <div className="db-live-metric">
                        <div className="db-live-value">{displayStats.totalPaidCheckouts}</div>
                        <div className="db-live-label">
                          <i className="fa fa-credit-card"></i>
                          Paid at Checkout
                        </div>
                      </div>
                    </Col>
                    <Col lg="6" md="12" sm="6" xs="6">
                      <div className="db-live-metric">
                        <div className="db-live-value">{displayStats.totalWhitelistedCheckouts}</div>
                        <div className="db-live-label">
                          <i className="fa fa-list-alt"></i>
                          Whitelisted at Checkout
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>

              {/* Second Column: Ticket Data */}
              <Col lg="6" md="6" sm="12">
                <div className="db-live-group">
                  <div className="db-live-group-title">
                    <i className="fa fa-ticket"></i>
                    Ticket Activity
                  </div>
                  <Row className="g-2">
                    <Col lg="6" md="12" sm="6" xs="6">
                      <div className="db-live-metric">
                        <div className="db-live-value">{displayStats.totalTicketSalesCount}</div>
                        <div className="db-live-label">
                          <i className="fa fa-shopping-cart"></i>
                          Visitor Tickets
                        </div>
                      </div>
                    </Col>
                    <Col lg="6" md="12" sm="6" xs="6">
                      <div className="db-live-metric">
                        <div className="db-live-value">{formatCurrency(displayStats.totalTicketRevenue)}</div>
                        <div className="db-live-label">
                          <i className="fa fa-dollar"></i>
                          Ticket Revenue
                        </div>
                      </div>
                    </Col>
                    <Col lg="6" md="12" sm="6" xs="6">
                      <div className="db-live-metric">
                        <div className="db-live-value">{displayStats.totalUsedTickets}</div>
                        <div className="db-live-label">
                          <i className="fa fa-check-square-o"></i>
                          Validated Tickets
                        </div>
                      </div>
                    </Col>
                    <Col lg="6" md="12" sm="6" xs="6">
                      <div className="db-live-metric">
                        <div className="db-live-value">{displayStats.totalUnusedTickets}</div>
                        <div className="db-live-label">
                          <i className="fa fa-square-o"></i>
                          Not Yet Validated
                        </div>
                      </div>
                    </Col>
                    <Col lg="6" md="12" sm="6" xs="6">
                      <div className="db-live-metric">
                        <div className="db-live-value">{displayStats.totalVerifications}</div>
                        <div className="db-live-label">
                          <i className="fa fa-check-circle-o"></i>
                          Scanned Badges
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </div>

      {/* Second Section: Three Charts - Each in its own row */}
      <div className="analytics-charts">
        {loading ? (
          <div>
            {Array.from({ length: 3 }).map((_, index) => (
              <Row key={index} className="mb-4">
                <Col xs="12">
                  <div className="db-chart-card">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="db-skeleton" style={{ height: "20px", width: "45%" }}></div>
                      <div className="db-skeleton" style={{ width: "36px", height: "36px", borderRadius: "8px" }}></div>
                    </div>
                    <div className="db-skeleton" style={{ height: "300px", width: "100%" }}></div>
                  </div>
                </Col>
              </Row>
            ))}
          </div>
        ) : (
          <div>
            {/* Tickets Chart - Full Row */}
            <Row className="mb-4">
              <Col xs="12">
                <div className="db-chart-card">
                  <div className="db-chart-head">
                    <div>
                      <h5 className="db-chart-title db-text-brand mb-1">
                        <i className="fa fa-shopping-cart me-2"></i>
                        Ticket Sales Analytics
                      </h5>
                      <p className="db-chart-sub">Daily ticket sales trends over selected period</p>
                    </div>
                    <div className="db-chart-total">
                      <div className="db-chart-total-value db-text-brand">{totalTicketSales}</div>
                      <div className="db-chart-total-label">Tickets Sold</div>
                      <div className="db-text-emerald fw-bold mt-1" style={{ fontSize: "0.85rem" }}>
                        <i className="fa fa-dollar-sign me-1"></i>
                        {formatCurrency(displayStats.totalTicketRevenue)}
                      </div>
                    </div>
                  </div>
                  <div style={{ height: "340px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={displayStats.ticketTrends} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
                        <defs>
                          <linearGradient id="ticketGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#4f46e5" stopOpacity={0.45} />
                          </linearGradient>
                        </defs>
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 12, fill: "#98a2b3" }}
                          tickFormatter={(value) =>
                            new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                          }
                          angle={-45}
                          textAnchor="end"
                          height={55}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis tick={{ fontSize: 12, fill: "#98a2b3" }} axisLine={false} tickLine={false} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#fff",
                            border: "1px solid #e6e8f0",
                            borderRadius: "10px",
                            boxShadow: "0 8px 20px rgba(16,24,40,0.09)",
                          }}
                          formatter={(value: any) => [value, "Tickets Sold"]}
                          labelFormatter={(label) =>
                            new Date(label).toLocaleDateString("en-US", {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })
                          }
                        />
                        <Bar dataKey="newBought" fill="url(#ticketGradient)" radius={[4, 4, 0, 0]} maxBarSize={34} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Vehicles Chart - Full Row */}
            <Row className="mb-4">
              <Col xs="12">
                <div className="db-chart-card">
                  <div className="db-chart-head">
                    <div>
                      <h5 className="db-chart-title db-text-emerald mb-1">
                        <i className="fa fa-car me-2"></i>
                        Vehicle Entry Analytics
                      </h5>
                      <p className="db-chart-sub">Daily vehicle entries and revenue trends</p>
                    </div>
                    <div className="db-chart-total">
                      <div className="db-chart-total-value db-text-emerald">{totalVehicleEntries}</div>
                      <div className="db-chart-total-label">Total Entries</div>
                      <div className="db-text-brand fw-bold mt-1" style={{ fontSize: "0.85rem" }}>
                        <i className="fa fa-dollar-sign me-1"></i>
                        {formatCurrency(displayStats.totalVehicleRevenue)}
                      </div>
                    </div>
                  </div>
                  <div style={{ height: "340px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={displayStats.vehicleTrends} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
                        <defs>
                          <linearGradient id="vehicleGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#059669" stopOpacity={0.3} />
                            <stop offset="100%" stopColor="#059669" stopOpacity={0.03} />
                          </linearGradient>
                        </defs>
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 12, fill: "#98a2b3" }}
                          tickFormatter={(value) =>
                            new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                          }
                          angle={-45}
                          textAnchor="end"
                          height={55}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis tick={{ fontSize: 12, fill: "#98a2b3" }} axisLine={false} tickLine={false} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#fff",
                            border: "1px solid #e6e8f0",
                            borderRadius: "10px",
                            boxShadow: "0 8px 20px rgba(16,24,40,0.09)",
                          }}
                          formatter={(value: any) => [value, "Vehicle Entries"]}
                          labelFormatter={(label) =>
                            new Date(label).toLocaleDateString("en-US", {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })
                          }
                        />
                        <Area type="monotone" dataKey="entered" stroke="#059669" fill="url(#vehicleGradient)" strokeWidth={3} />
                        <Line
                          type="monotone"
                          dataKey="entered"
                          stroke="#059669"
                          strokeWidth={3}
                          dot={{ fill: "#059669", strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6, stroke: "#059669", strokeWidth: 2, fill: "#fff" }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Badge Verifications Chart - Full Row */}
            <Row className="mb-4">
              <Col xs="12">
                <div className="db-chart-card">
                  <div className="db-chart-head">
                    <div>
                      <h5 className="db-chart-title db-text-cyan mb-1">
                        <i className="fa fa-shield-alt me-2"></i>
                        Badge Verification Analytics
                      </h5>
                      <p className="db-chart-sub">Daily badge verification trends and patterns</p>
                    </div>
                    <div className="db-chart-total">
                      <div className="db-chart-total-value db-text-cyan">{totalVerifications}</div>
                      <div className="db-chart-total-label">Badges Scanned</div>
                      <div className="db-text-amber fw-bold mt-1" style={{ fontSize: "0.85rem" }}>
                        <i className="fa fa-print me-1"></i>
                        {displayStats.totalBadgePrints} Printed
                      </div>
                    </div>
                  </div>
                  <div style={{ height: "340px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={displayStats.badgeVerifications}
                        margin={{ top: 10, right: 20, left: 10, bottom: 40 }}
                      >
                        <defs>
                          <linearGradient id="badgeGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#0891b2" stopOpacity={0.55} />
                            <stop offset="100%" stopColor="#0891b2" stopOpacity={0.05} />
                          </linearGradient>
                        </defs>
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 12, fill: "#98a2b3" }}
                          tickFormatter={(value) =>
                            new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                          }
                          angle={-45}
                          textAnchor="end"
                          height={55}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis tick={{ fontSize: 12, fill: "#98a2b3" }} axisLine={false} tickLine={false} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#fff",
                            border: "1px solid #e6e8f0",
                            borderRadius: "10px",
                            boxShadow: "0 8px 20px rgba(16,24,40,0.09)",
                          }}
                          formatter={(value: any) => [value, "Verifications"]}
                          labelFormatter={(label) =>
                            new Date(label).toLocaleDateString("en-US", {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })
                          }
                        />
                        <Area
                          type="monotone"
                          dataKey="verifications"
                          stroke="#0891b2"
                          fill="url(#badgeGradient)"
                          strokeWidth={2}
                          dot={{ fill: "#0891b2", strokeWidth: 2, r: 3 }}
                          activeDot={{ r: 5, stroke: "#0891b2", strokeWidth: 2, fill: "#fff" }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </div>
  )
}

export default AnalyticalData
